import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getMyProjectorStateV19, getProjectorStateV16 } from '../lib/api';
import { errorMessage, formatDate, formatTime } from '../lib/utils';
import type { ProjectorState } from '../types';
import { useRealtimeSignal } from '../hooks/useRealtimeSignal';
import { generateBrandedQrDataUrl } from '../lib/qr';

export default function ProjectorPage() {
  const { meetingId = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<ProjectorState | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [errorText, setErrorText] = useState('');
  const [privacy, setPrivacy] = useState(() => localStorage.getItem('mtib_projector_privacy') === '1');
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [wakeActive, setWakeActive] = useState(false);
  const wakeRef = useRef<any>(null);
  const audioRef = useRef<AudioContext | null>(null);
  const latestCheckinTimeRef = useRef<string | null>(null);

  const playChime = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = audioRef.current;
    if (!ctx) return;
    const now = ctx.currentTime;
    [659.25, 783.99].forEach((frequency, index) => {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0.0001, now + index * 0.11);
      gain.gain.exponentialRampToValueAtTime(0.08, now + index * 0.11 + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + index * 0.11 + 0.17);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(now + index * 0.11);
      oscillator.stop(now + index * 0.11 + 0.18);
    });
  }, [soundEnabled]);

  const load = useCallback(async (chimeOnNew = false) => {
    try {
      const next = token
        ? await getProjectorStateV16(meetingId, token)
        : await getMyProjectorStateV19(meetingId);
      const newestCheckinTime = next.latest[0]?.checkin_time ?? null;
      const previousCheckinTime = latestCheckinTimeRef.current;
      if (chimeOnNew && newestCheckinTime && previousCheckinTime && Date.parse(newestCheckinTime) > Date.parse(previousCheckinTime)) playChime();
      if (newestCheckinTime && (!previousCheckinTime || Date.parse(newestCheckinTime) > Date.parse(previousCheckinTime))) {
        latestCheckinTimeRef.current = newestCheckinTime;
      }
      setState(next);
      setErrorText('');
    } catch (error) { setErrorText(errorMessage(error)); }
  }, [meetingId, playChime, token]);

  useEffect(() => { void load(false); }, [load]);
  useRealtimeSignal(useCallback(() => { void load(true); }, [load]), meetingId);

  useEffect(() => {
    const healingPoll = window.setInterval(() => { if (!document.hidden) void load(true); }, 10000);
    return () => window.clearInterval(healingPoll);
  }, [load]);

  useEffect(() => {
    generateBrandedQrDataUrl(`${window.location.origin}/checkin/${meetingId}`, { width: 1200 }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
  }, [meetingId]);

  const requestWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) { setWakeActive(false); return; }
    try {
      wakeRef.current = await (navigator as any).wakeLock.request('screen');
      setWakeActive(true);
      wakeRef.current.addEventListener('release', () => setWakeActive(false));
    } catch { setWakeActive(false); }
  }, []);

  useEffect(() => {
    void requestWakeLock();
    const visibility = () => { if (document.visibilityState === 'visible' && !wakeRef.current) void requestWakeLock(); };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      void wakeRef.current?.release?.();
    };
  }, [requestWakeLock]);

  const toggleSound = async () => {
    if (!soundEnabled) {
      const ctx = audioRef.current ?? new AudioContext();
      audioRef.current = ctx;
      if (ctx.state === 'suspended') await ctx.resume();
      setSoundEnabled(true);
    } else setSoundEnabled(false);
  };

  const togglePrivacy = () => {
    setPrivacy((current) => {
      localStorage.setItem('mtib_projector_privacy', current ? '0' : '1');
      return !current;
    });
  };

  if (errorText) return <main className="projector-page"><div className="projector-error"><h1>Paparan Projector tidak tersedia</h1><p>{errorText}</p></div></main>;
  if (!state) return <main className="projector-page"><div className="projector-loading">Memuatkan kehadiran langsung…</div></main>;
  const joinUrl = new URL('/', window.location.href).toString().replace(/\/$/, '');
  const meetingTime = formatTime(state.meeting.meeting_time);
  const latest = [...state.latest].filter((record) => record.is_valid !== false).sort((a, b) => Date.parse(b.checkin_time) - Date.parse(a.checkin_time));

  return (
    <main className="projector-page">
      <div className="projector-grid">
        <section className="projector-qr-panel" aria-labelledby="projector-meeting-title">
          <div className="projector-qr-stage">
            <div className="projector-qr-frame">{qrDataUrl && <img src={qrDataUrl} alt="Kod QR check-in mesyuarat" />}</div>
            <div className="projector-instructions">
              <h2>SCAN</h2>
              <ol>
                <li>Masukkan E-mel</li>
                <li>Masukkan OTP daripada e-mel MTIB</li>
                <li>Sahkan</li>
                <li className="instruction-done">✓ SELESAI</li>
              </ol>
              <div className="projector-code-divider" aria-hidden="true"><span>ATAU</span></div>
              <div className="projector-code-panel">
                <strong>MELALUI KOD</strong>
                <span>Layari:</span>
                <b className="projector-join-url">{joinUrl}</b>
                <span>Kod Mesyuarat</span>
                <b>{state.meeting.join_code ?? '—'}</b>
              </div>
            </div>
          </div>
          <div className="projector-meeting-details">
            <h1 id="projector-meeting-title">{state.meeting.title}</h1>
            {state.meeting.chairman && <p>Pengerusi: {state.meeting.chairman}</p>}
            <p>{formatDate(state.meeting.meeting_date)} · {meetingTime}</p>
            {state.meeting.location && <p>{state.meeting.location}</p>}
          </div>
        </section>

        <section className="projector-live-panel">
          <div className="projector-toolbar" aria-label="Kawalan Projector">
            <button onClick={requestWakeLock} className={wakeActive ? 'toolbar-on' : ''}>{wakeActive ? 'Kunci skrin: ON' : 'Kunci skrin'}</button>
            <button onClick={toggleSound} className={soundEnabled ? 'toolbar-on' : ''}>{soundEnabled ? 'Bunyi: ON' : 'Bunyi: SENYAP'}</button>
            <button onClick={togglePrivacy} className={privacy ? 'toolbar-on' : ''}>{privacy ? 'Privasi: ON' : 'Privasi: OFF'}</button>
            <button onClick={() => document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()}>Skrin penuh</button>
          </div>
          <div className="projector-counters">
            <article><span>Kakitangan Dalaman MTIB</span><strong>{state.internal_count}</strong></article>
            <article><span>Tetamu Luaran</span><strong>{state.guest_count}</strong></article>
          </div>
          <div className="activity-panel">
            <div className="activity-title"><h2>Check-in Terkini</h2><span className="live-dot">LANGSUNG</span></div>
            <div className="activity-list">
              {latest.length ? latest.map((record) => (
                <div className="activity-item" key={record.record_id}>
                  <div className="activity-avatar">{record.is_internal ? 'M' : 'G'}</div>
                  <div className="activity-copy">
                    <strong>{privacy ? (record.is_internal ? 'Kakitangan MTIB' : 'Tetamu Luaran') : record.snapshot_name}</strong>
                    <span>{privacy ? (record.is_internal ? 'MTIB' : 'Tetamu') : record.snapshot_dept}</span>
                  </div>
                  <span className="activity-time">{new Intl.DateTimeFormat('en-MY', { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kuala_Lumpur' }).format(new Date(record.checkin_time))}</span>
                </div>
              )) : <div className="activity-empty">Menunggu Check-in pertama…</div>}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
